import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Lock, Loader2, UserPlus } from 'lucide-react';
import { registerUser } from '../api';
import type { RegistrationPrefill } from '../api';
import { getErrorMessage } from '../api/NetworkManager';
import { useAuth } from '../context/AuthContext';

interface NavState {
  registrationToken: string;
  profile: RegistrationPrefill;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applyAuth } = useAuth();

  const navState = (location.state ?? null) as NavState | null;

  // If user lands here without a registration token, send them to login
  useEffect(() => {
    if (!navState?.registrationToken) {
      navigate('/login', { replace: true });
    }
  }, [navState, navigate]);

  const prefill = navState?.profile;

  const [firstName, setFirstName] = useState(prefill?.firstName ?? '');
  const [lastName, setLastName] = useState(prefill?.lastName ?? '');
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!navState || !prefill) {
    return null; // redirecting
  }

  const giamLocked = {
    firstName: !!prefill.firstName,
    lastName: !!prefill.lastName,
    email: !!prefill.email,
    jobRole: !!prefill.jobRole,
    company: !!prefill.company,
    brand: !!prefill.brand,
    bmu: !!prefill.bmu,
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting) return;
    setError(null);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      setError('First and last name are required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await registerUser({
        registrationToken: navState!.registrationToken,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        displayName: displayName.trim() || undefined,
      });
      applyAuth(
        {
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          expiresIn: result.expiresIn,
          tokenType: 'Bearer',
        },
        result.profile,
      );
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-orange-400/20 via-red-500/10 to-transparent blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-[500px] w-[500px] rounded-full bg-gradient-to-tl from-purple-600/15 via-red-500/10 to-transparent blur-3xl" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-3">
          <img src="/logo.svg" alt="Byte" className="h-9" />
          <span className="font-display text-lg font-bold tracking-tight text-ink">Byte Hackathon</span>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-ring/60 bg-surface-raised/80 p-8 shadow-card backdrop-blur-sm"
        >
          <div className="mb-6">
            <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-ring/60 bg-surface px-2.5 py-0.5 text-[11px] font-medium text-ink-secondary">
              <UserPlus className="h-3 w-3" />
              First sign-in
            </div>
            <h1 className="font-display text-2xl font-bold text-ink">Complete your profile</h1>
            <p className="mt-1 text-sm text-ink-secondary">
              We've pre-filled what we know from your Yum profile. Confirm or edit anything that's missing.
            </p>
          </div>

          <div className="space-y-4">
            <ReadOnlyField label="Yum ID" value={prefill.yumId} locked />
            <ReadOnlyField label="Email" value={prefill.email} locked={giamLocked.email} />

            <div className="grid grid-cols-2 gap-3">
              <EditableField
                label="First name"
                value={firstName}
                onChange={setFirstName}
                locked={giamLocked.firstName}
                required
              />
              <EditableField
                label="Last name"
                value={lastName}
                onChange={setLastName}
                locked={giamLocked.lastName}
                required
              />
            </div>

            <EditableField
              label="Display name (optional)"
              value={displayName}
              onChange={setDisplayName}
              placeholder={`${firstName} ${lastName}`.trim() || 'How you want to be known'}
            />

            {(prefill.jobRole || prefill.company || prefill.brand || prefill.bmu) && (
              <div className="rounded-xl border border-ring/40 bg-surface/60 p-4">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
                  From your Yum profile
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {prefill.jobRole && <ReadOnlyField label="Job role" value={prefill.jobRole} locked compact />}
                  {prefill.company && <ReadOnlyField label="Company" value={prefill.company} locked compact />}
                  {prefill.brand && <ReadOnlyField label="Brand" value={prefill.brand} locked compact />}
                  {prefill.bmu && <ReadOnlyField label="Business Market Unit" value={prefill.bmu} locked compact />}
                  {prefill.department && <ReadOnlyField label="Department" value={prefill.department} locked compact />}
                  {prefill.workCountry && <ReadOnlyField label="Work country" value={prefill.workCountry} locked compact />}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-5 rounded-lg border border-status-error/30 bg-status-error/5 px-3 py-2 text-sm text-status-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !firstName.trim() || !lastName.trim()}
            className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 via-red-500 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-red-500/20 transition-all hover:shadow-xl hover:shadow-red-500/30 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Continue <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  locked,
  compact,
}: {
  label: string;
  value: string;
  locked?: boolean;
  compact?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-secondary">
        {label}
        {locked && <Lock className="h-3 w-3 text-ink-tertiary" />}
      </label>
      <div
        className={`input-base w-full cursor-not-allowed select-text bg-surface/60 text-ink-secondary ${
          compact ? 'py-1.5 text-sm' : ''
        }`}
      >
        {value || '—'}
      </div>
    </div>
  );
}

function EditableField({
  label,
  value,
  onChange,
  locked,
  required,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  locked?: boolean;
  required?: boolean;
  placeholder?: string;
}) {
  if (locked) return <ReadOnlyField label={label} value={value} locked />;
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-secondary">
        {label}
        {required && <span className="ml-0.5 text-status-error">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="input-base w-full"
      />
    </div>
  );
}
